import { useEffect, useRef } from 'react';

export function useDialogFocus(active: boolean, onClose?: () => void, locked = false) {
  const latest = useRef({ onClose, locked });
  latest.current = { onClose, locked };
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active || !ref.current) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = ref.current;
    dialog.focus({ preventScroll: true });
    if (latest.current.onClose) {
      dialog
        .querySelector<HTMLElement>(
          'input:not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled)',
        )
        ?.focus({ preventScroll: true });
    }
    const trapTab = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && latest.current.onClose) {
        event.preventDefault();
        event.stopPropagation();
        if (!latest.current.locked) latest.current.onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], summary, [tabindex="0"]',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === dialog)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('keydown', trapTab);
    return () => {
      dialog.removeEventListener('keydown', trapTab);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [active]);
  return ref;
}
